import type { SurfDeck, SurfLayer, SurfSlide, TextRole } from "@vida/surf-core";
import { updateLayerInDeck, updateSlideInDeck } from "@vida/surf-core";

type Props = {
  deck: SurfDeck;
  slide: SurfSlide;
  onDeckChange: (deck: SurfDeck) => void;
};

const ROLE_LABEL: Record<string, string> = {
  title: "Titolo",
  subtitle: "Sottotitolo",
  body: "Testo",
  bullet: "Punto",
  caption: "Didascalia",
  kicker: "Kicker",
};

/** Edit the slide's base text (its outline) directly from the design interface. */
export function SlideOutlinePanel({ deck, slide, onDeckChange }: Props) {
  const textLayers = slide.layers.filter((l) => l.type === "text");
  const ordered = [...textLayers].sort((a, b) => roleRank(a.textRole) - roleRank(b.textRole));

  const patchLayer = (layer: SurfLayer, content: string) => {
    onDeckChange(updateLayerInDeck(deck, slide.id, layer.id, { content }));
  };

  const patchNotes = (notes: string) => {
    onDeckChange(updateSlideInDeck(deck, slide.id, (s) => ({ ...s, speakerNotes: notes })));
  };

  return (
    <section className="slide-outline">
      <p className="muted inspector-hint">
        Testo base della slide. Le modifiche aggiornano direttamente i widget di testo.
      </p>

      {ordered.length === 0 && <p className="muted">Nessun testo in questa slide.</p>}

      {ordered.map((layer) => {
        const role = layer.textRole ?? "body";
        const multiline = role === "body" || role === "bullet";
        return (
          <label className="field-label" key={layer.id}>
            {ROLE_LABEL[role] ?? role}
            {multiline ? (
              <textarea
                className="inspector-textarea"
                rows={role === "body" ? 3 : 2}
                value={layer.content ?? ""}
                onChange={(e) => patchLayer(layer, e.target.value)}
              />
            ) : (
              <input
                className="text-input"
                value={layer.content ?? ""}
                onChange={(e) => patchLayer(layer, e.target.value)}
              />
            )}
          </label>
        );
      })}

      <label className="field-label">
        Note relatore
        <textarea
          className="inspector-textarea"
          rows={2}
          value={slide.speakerNotes ?? ""}
          placeholder="Note non visibili nella slide"
          onChange={(e) => patchNotes(e.target.value)}
        />
      </label>
    </section>
  );
}

function roleRank(role?: TextRole): number {
  const order: Record<string, number> = {
    kicker: 0,
    title: 1,
    subtitle: 2,
    body: 3,
    bullet: 4,
    caption: 5,
  };
  return order[role ?? "body"] ?? 9;
}
